import { createSelector } from '@reduxjs/toolkit';

import { pickableLicences } from '@/features/licence/licence-picker';
import type { RootState } from '@/store/root-reducer';

import { validateAddDraft } from '../domain/add-user.validation';

const selectAddUser = (state: RootState) => state.addUser;

export const selectDraft = (state: RootState) => selectAddUser(state).draft;
export const selectLicences = (state: RootState) => selectAddUser(state).licences;
export const selectPresetLicenceId = (state: RootState) => selectAddUser(state).presetLicenceId;
export const selectSaveStatus = (state: RootState) => selectAddUser(state).saveStatus;
export const selectCreated = (state: RootState) => selectAddUser(state).created;
export const selectError = (state: RootState) => selectAddUser(state).error;
export const selectValidationErrors = (state: RootState) => selectAddUser(state).validationErrors;

export const selectIsSaving = (state: RootState) => selectAddUser(state).saveStatus === 'saving';

/** Derived: is the current draft valid (identity + licence)? Memoised on the draft. */
export const selectIsDraftValid = createSelector(
  selectDraft,
  (draft) => Object.keys(validateAddDraft(draft)).length === 0,
);

/** Derived: why the LinkedIn URL is refused, if it is (#591). Memoised on the draft. */
export const selectLinkedInUrlError = createSelector(
  selectDraft,
  (draft) => validateAddDraft(draft).linkedinUrl,
);

/** Derived: the display name of the preset licence (for the disabled field). */
export const selectPresetLicenceName = createSelector(
  [selectLicences, selectPresetLicenceId],
  (licences, presetId) => licences.find((l) => l.id === presetId)?.name,
);

/** Derived: the licences the picker offers: the deactivated ones are hidden (#592). */
export const selectPickableLicences = createSelector(selectLicences, pickableLicences);

/**
 * Derived: the licence the draft points at, for the summary under the field:
 * the picked one, or the preset. Looked up in every loaded licence, not only the
 * pickable ones, so a preset is found whatever its status.
 */
export const selectSelectedLicence = createSelector(
  [selectLicences, selectDraft],
  (licences, draft) => licences.find((l) => l.id === draft.licenceId),
);
